// BrandLockup — how the generated brand assets reach the screen.
//
// Per `DESIGN.md §14.1` + `ADR-0129`. Two decisions here are load-bearing, and
// both are the kind that look like style:
//
// 1. **The mark is an `<img src="/brand/logo-mark.svg">`, not inlined SVG
//    markup.** Inlining would put `#6EA5DC` into a `.tsx` file, and `§3 #17`
//    bans hardcoded colour in UI code — the brand hexes would become a second,
//    untokenised copy of `--you` / `--partner` / `--shared`, and nothing would
//    notice when a token moved. An `<img>` keeps every hex in exactly one
//    place: `scripts/build-brand-assets.mjs`.
//
//    The cost is honest and worth naming: the mark cannot inherit `currentColor`
//    or a CSS filter, so this component has no monochrome mode. For that case
//    the file `logo-mark-mono.svg` exists and `mono` selects it.
//
// 2. **The mark's `alt` is `""` and the wordmark is live text.** The link is
//    already named by the wordmark that follows it; a second name from an `alt`
//    would make the accessible name `"HESHE HESHE"`, and
//    `touch-target-floor.test.tsx` asserts it is exactly `"HESHE"`. The mark is
//    decoration *here* — `mark-only` is the variant that has to carry a name,
//    because on its own it is the only thing naming anything.
//
// The wordmark and the slogan come from `./brandCopy`, not from literals here.
// A component that spells `heshe` inline is one more place to update when the
// wordmark is respelled, and `grep` for the string would then find two answers.

import { BRAND_SLOGAN, BRAND_WORDMARK } from "./brandCopy";

const MARK_SRC = {
  color: "/brand/logo-mark.svg",
  mono: "/brand/logo-mark-mono.svg",
} as const;

export type BrandMarkSize = "sm" | "md" | "lg";
export type BrandLockupVariant = "lockup" | "mark-only" | "stacked";

/**
 * Sized by **ink height**, and `w-auto` so the 120:105 aspect holds.
 *
 * `sm` is **20px**, up from 16px on 2026-10-08. The bump came with the wordmark
 * going to `text-h2` (20px): the old pairing was `sm` 16px against `text-h3`
 * 17px, which lined the mark's ink up with the lowercase ascender. Caps have no
 * descender to sit in and their cap-height is nearer the full em, so the mark
 * grew with them — keeping it at 16px would have left a 20px word beside a
 * 16px mark, and the pair stops reading as one lockup.
 *
 * The number here **is** the rendered size: the mark SVG frames itself to its
 * ink bounds (`DESIGN.md §14.1.6`). The first pass used `w-5` — 20px — which
 * sounds close and is not, because that set the *width* rather than the ink
 * height; at that size the 4× header screenshot showed a speck beside the
 * wordmark, not a lockup.
 */
const MARK_BOX = {
  sm: "h-5 w-auto",
  md: "h-6 w-auto",
  lg: "h-12 w-auto",
} as const;

/** Intrinsic size of `logo-mark.svg` — the generator frames it to its ink. */
const MARK_INTRINSIC = { w: 120, h: 105 };

export interface BrandLockupProps {
  variant?: BrandLockupVariant;
  size?: BrandMarkSize;
  /** Uses `logo-mark-mono.svg`. Only meaningful with `variant="mark-only"`. */
  mono?: boolean;
  className?: string;
  /** Overrides the mark's size box. Escape hatch; tokens are the default. */
  markClassName?: string;
  /**
   * `stacked` only. Defaults to `BRAND_SLOGAN`. Exists so a caller can
   * **suppress** it (`slogan=""`) without a second variant name, and so the one
   * place the copy lives stays the one place — see `brandCopy.ts`.
   */
  slogan?: string;
}

export function BrandLockup({
  variant = "lockup",
  size = "sm",
  mono = false,
  className,
  markClassName,
  slogan = BRAND_SLOGAN,
}: BrandLockupProps) {
  const src = mono ? MARK_SRC.mono : MARK_SRC.color;
  const mark = (alt: string) => (
    <img
      src={src}
      alt={alt}
      width={MARK_INTRINSIC.w}
      height={MARK_INTRINSIC.h}
      className={`${MARK_BOX[size]} object-contain shrink-0 ${markClassName ?? ""}`.trim()}
    />
  );

  if (variant === "mark-only") {
    return (
      <img
        src={src}
        alt={BRAND_WORDMARK}
        width={MARK_INTRINSIC.w}
        height={MARK_INTRINSIC.h}
        className={`${MARK_BOX[size]} object-contain ${markClassName ?? ""} ${className ?? ""}`.trim()}
      />
    );
  }

  // `stacked` — mark, wordmark, slogan on three centred lines.
  //
  // The two gaps are **not** the same, and that is the whole layout. The mark
  // is a graphic with optical clear space built around it; the wordmark is the
  // thing it identifies, so they sit closest (`s-2`, 8px). The slogan is a third
  // element, not a continuation of the pair, so it steps in tighter (`s-1`,
  // 4px) — the rhythm tightens as it descends, which is what makes three lines
  // read as one lockup instead of three stacked objects. A uniform gap here is
  // the version that looks accidental, and it is the version I wrote first.
  //
  // **The slogan is `text-ink-2`, not `text-ink-3`.** `DESIGN.md §2.3`'s own
  // matrix measures `ink-3` at 3.22:1 on paper and **2.85:1** on the tinted
  // hero — below even the 3:1 non-text floor, so it is not "a bit pale", it is
  // non-compliant. `ink-2` measures 6.21:1 and is the documented substitute.
  if (variant === "stacked") {
    return (
      <span
        className={`flex flex-col items-center ${className ?? ""}`.trim()}
        data-testid="brand-stacked-lockup"
      >
        <span className="flex flex-col items-center gap-s-2">
          {mark("")}
          <span className="font-display text-h2 text-ink-1">{BRAND_WORDMARK}</span>
        </span>
        {slogan ? (
          <span className="mt-s-1 text-body-sm text-ink-2 text-center">{slogan}</span>
        ) : null}
      </span>
    );
  }

  // `gap-s-2` (the design system's 8px) rather than a bare `gap-2`: the mark's
  // ink is 120 units wide with clear space built in, so a slightly wider gutter
  // is what makes the pair read as one lockup rather than an icon beside a word.
  return (
    <span className={`inline-flex items-center gap-s-2 ${className ?? ""}`.trim()}>
      {mark("")}
      <span className="font-display text-h2 text-ink-1">{BRAND_WORDMARK}</span>
    </span>
  );
}

